import { Component, Fragment, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui';
import './AuthenticatedRouteErrorBoundary.css';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  correlationId: string;
  hasError: boolean;
  retryKey: number;
}

class RouteErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = {
    correlationId: '',
    hasError: false,
    retryKey: 0,
  };

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return {
      correlationId: crypto.randomUUID(),
      hasError: true,
    };
  }

  override componentDidCatch(): void {
    console.error('authenticated_route.render_failed', {
      correlationId: this.state.correlationId,
    });
  }

  private retry = (): void => {
    this.setState(state => ({
      correlationId: '',
      hasError: false,
      retryKey: state.retryKey + 1,
    }));
  };

  override render() {
    if (this.state.hasError) {
      return (
        <section className="route-error" role="alert" aria-labelledby="route-error-heading">
          <AlertTriangle aria-hidden="true" />
          <h1 id="route-error-heading">We couldn&apos;t open this page</h1>
          <p>Something unexpected happened. Try the page again or return to Text translation.</p>
          <div className="route-error-actions">
            <Button type="button" onClick={this.retry}>
              Try again
            </Button>
            <Link className="route-error-link" to="/">
              Return to Text translation
            </Link>
          </div>
          <p className="route-error-reference">Reference: {this.state.correlationId}</p>
        </section>
      );
    }

    return <Fragment key={this.state.retryKey}>{this.props.children}</Fragment>;
  }
}

export function AuthenticatedRouteErrorBoundary({ children }: ErrorBoundaryProps) {
  const location = useLocation();

  return <RouteErrorBoundary key={location.pathname}>{children}</RouteErrorBoundary>;
}
